import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { takeSessionId } from '@renderer/utils/sessionIdSource'
import cogoToast from '@successtar/cogo-toast'
import { FaQuestionCircle, FaGift } from 'react-icons/fa'
import { RecordingType } from '@renderer/types/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const Welcome: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const [showPromoModal, setShowPromoModal] = useState(false)
  const [discount] = useState(0)
  const { selectedSecondaryCamera } = useSettingsStore()
  const { setSessionState, uploadSessionState } = useSessionStateStore()
  const { config, getPageSettings } = useConfigStore()
  const [isSessionStarting, setIsSessionStarting] = useState(false)

  const startSession = async (): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      // Credit deduction is now handled in Capture.tsx when capture session starts
      // This ensures customers only pay when they actually start capturing

      const sessionId = takeSessionId()

      const recordingType = config?.pages?.checkout?.settings?.packages?.find(
        (pkg) =>
          pkg.type === 'recording' ||
          pkg.type === 'strip_live' ||
          pkg.type === 'strip_story' ||
          pkg.type === 'session_recording'
      )?.type as RecordingType

      setSessionState({
        id: sessionId,
        projectId,
        mode: 'capture',
        timeline: {
          startTime: new Date(),
          activityLogs: [
            {
              page: 'welcome',
              type: 'session',
              action: 'start',
              details: {
                mode: 'capture',
                discount: discount
              },
              timestamp: new Date()
            }
          ]
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        },
        transaction: {
          discount: discount
        }
      })

      let nextPage = 'frame'
      if (selectedSecondaryCamera) {
        nextPage = 'camera'
      }
      uploadSessionState()
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error('Error starting session')
    } finally {
      setIsSessionStarting(false)
    }
  }

  const handleTutorialClick = (): void => {
    // TODO: Show tutorial modal or navigate to tutorial page when tutorial image is available
    cogoToast.info('Tutorial akan segera hadir!')
  }

  const handleVoucherClick = (): void => {
    setShowPromoModal(true)
    // TODO: Show promo modal when implemented
    cogoToast.info('Voucher feature akan segera hadir!')
  }

  // Call all hooks at the top level
  const cachedWelcomeBg = useCachedBackgroundImage('/assets/yotta/welcome.webp')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{
        backgroundImage: `url(${cachedWelcomeBg})`
      }}
    >
      {/* Main Content Area - Right Side */}
      <div className="absolute right-[400px] text-center top-1/2 transform -translate-y-1/2 flex flex-col items-center">
        {/* Title */}
        <h1 className="font-title-yotta text-beigeYotta text-[128px] leading-none mb-4">
          Photobooth
        </h1>

        {/* Start Here Button */}
        <button
          onClick={startSession}
          disabled={isSessionStarting}
          className="font-body-yotta text-blueGrayYotta text-[36px] hover:opacity-80 transition-opacity active:scale-95 transform"
        >
          Start Here →
        </button>
      </div>

      {/* Bottom Buttons */}
      <div className="absolute bottom-[80px] left-1/3 right-0 flex justify-between px-[120px]">
        {/* Panduan Button */}
        <button
          onClick={handleTutorialClick}
          className="flex items-center gap-3 bg-beigeYotta text-white px-6 py-4 rounded-lg hover:brightness-110 transition-all shadow-lg font-button-yotta font-bold text-2xl"
        >
          PANDUAN
          <FaQuestionCircle size={24} />
        </button>

        {/* Voucher Button */}
        <button
          onClick={handleVoucherClick}
          className="flex items-center gap-3 bg-beigeYotta text-white px-6 py-4 rounded-lg hover:brightness-110 transition-all shadow-lg font-button-yotta font-bold text-2xl"
        >
          <FaGift size={24} />
          VOUCHER
        </button>
      </div>

      {/* TODO: Add PromoModal component when implemented */}
      {showPromoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg">
            <p>Promo Modal - Coming Soon</p>
            <button
              onClick={() => setShowPromoModal(false)}
              className="mt-4 px-4 py-2 bg-gray-300 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Welcome
